// Copyright 2025-2026 NXGN Management, LLC. All Rights Reserved.
import { Form, FormItem, Modal, UseShowHide, Input } from '@teamnine/pcj-ui-v2'

import { Template as ITemplate } from '@/services/templates'
import { useErrorHelper } from '@/app/shared/hooks/useErrorHelper'
import { useUpdateTemplate } from '@/app/Templates/hooks/useUpdateTemplate'

type UpdateTemplateModalProps = {
  modal: UseShowHide
  template?: ITemplate
}

const FORM_NAME = 'update-template'

export default function UpdateTemplateModal({
  modal,
  template,
}: UpdateTemplateModalProps) {
  const { basicErrorHandler, displayGlobalMessage } = useErrorHelper()
  const { mutateAsync: saveTemplate, isLoading } = useUpdateTemplate()

  const onFormFinish = async (values: {
    name: string
    description: string
  }) => {
    const { name, description } = values
    if (!template) return

    try {
      await saveTemplate({
        ...template,
        id: template?.id,
        name,
        description,
      })

      displayGlobalMessage({
        heading: 'Template successfully updated.',
        type: 'success',
      })
      modal.hide()
    } catch (err) {
      basicErrorHandler(err)
    }
  }

  return (
    <Modal open={modal.visible} onOpenChange={modal.toggle}>
      <Modal.Display aria-label="Update Template">
        <Modal.Header title="Update Template" />
        <Modal.Content>
          <Form name={FORM_NAME} onFinish={onFormFinish}>
            <FormItem
              required
              label="Template Name"
              name="name"
              initialValue={template?.name}
            >
              <Input placeholder="Enter template name" />
            </FormItem>
            <FormItem
              label="Template Description"
              name="description"
              initialValue={template?.description}
            >
              <Input placeholder="Enter template description" />
            </FormItem>
          </Form>
        </Modal.Content>
        <Modal.Footer
          footerAction={{
            primaryButton: {
              label: 'Update',
              type: 'submit',
              form: FORM_NAME,
              loading: isLoading,
            },
            secondaryButton: {
              label: 'Cancel',
              closeModal: true,
            },
          }}
        />
      </Modal.Display>
    </Modal>
  )
}
